/**
 * ContentReportsPage - Admin page for managing content reports
 * Only accessible by super_admin
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, Timestamp, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Flag, X, Clock, AlertTriangle, Eye, Trash2, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

interface ContentReport {
  id: string;
  contentType: 'post' | 'comment';
  contentId: string;
  contentAuthorId: string | null;
  reportedBy: string;
  reporterEmail: string | null;
  franchiseId: string | null;
  reason: string;
  additionalDetails: string | null;
  status: 'pending' | 'reviewed' | 'actioned' | 'dismissed';
  createdAt: Timestamp;
  reviewedAt: Timestamp | null;
  reviewedBy: string | null;
  action: string | null;
}

const STATUS_CONFIG = {
  pending: { color: 'bg-accent/10 text-accent bg-accent/10 text-accent', icon: Clock },
  reviewed: { color: 'bg-info/10 text-info bg-info/10 text-info', icon: Eye },
  actioned: { color: 'bg-danger/10 text-danger bg-danger/10 text-danger', icon: Ban },
  dismissed: { color: 'bg-surface-2 text-ink bg-surface-2 text-ink-muted', icon: X },
};

export default function ContentReportsPage() {
  const { t, i18n } = useTranslation();
  const { firebaseUser, customClaims } = useAuth();
  const [reports, setReports] = useState<ContentReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'reviewed' | 'actioned' | 'dismissed'>('all');
  const [selectedReport, setSelectedReport] = useState<ContentReport | null>(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);

  const dateLocale = i18n.language === 'es' ? es : enUS;

  // Check access
  useEffect(() => {
    if (customClaims?.role !== 'super_admin') {
      toast.error(t('common.accessDenied'));
    }
  }, [customClaims, t]);

  // Load reports
  useEffect(() => {
    const reportsRef = collection(db, 'content_reports');
    let q = query(reportsRef, orderBy('createdAt', 'desc'));

    if (filter !== 'all') {
      q = query(reportsRef, where('status', '==', filter), orderBy('createdAt', 'desc'));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ContentReport[];
      setReports(reportsData);
      setLoading(false);
    }, (error) => {
      console.error('Error loading reports:', error);
      toast.error(t('reports.loadError'));
      setLoading(false);
    });

    return () => unsubscribe();
  }, [filter, t]);

  const handleUpdateStatus = async (reportId: string, newStatus: ContentReport['status'], action?: string) => {
    try {
      const reportRef = doc(db, 'content_reports', reportId);
      await updateDoc(reportRef, {
        status: newStatus,
        reviewedAt: Timestamp.now(),
        reviewedBy: firebaseUser?.uid || null,
        action: action || null
      });
      toast.success(t('reports.statusUpdated'));
      setActionModalOpen(false);
      setSelectedReport(null);
    } catch (error) {
      console.error('Error updating report:', error);
      toast.error(t('reports.updateError'));
    }
  };

  const getReasonLabel = (reason: string) => {
    return t(`report.reasons.${reason}`, reason);
  };

  const pendingCount = reports.filter(r => r.status === 'pending').length;

  if (customClaims?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-danger mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-ink mb-2">
            {t('common.accessDenied')}
          </h1>
          <p className="text-ink-muted">
            {t('reports.superAdminOnly')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-danger/10 rounded-xl">
            <Flag className="w-6 h-6 text-danger" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ink">
              {t('reports.title')}
            </h1>
            <p className="text-sm text-ink-muted">
              {t('reports.subtitle')}
            </p>
          </div>
        </div>

        {pendingCount > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-accent" />
            <span className="text-sm font-medium text-accent">
              {pendingCount} {t('reports.pendingReports')}
            </span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        {(['all', 'pending', 'reviewed', 'actioned', 'dismissed'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === status
                ? 'bg-accent/100 text-on-accent'
                : 'bg-surface-2 text-ink-muted hover:bg-surface-3'
            }`}
          >
            {t(`reports.filter.${status}`)}
            {status === 'pending' && pendingCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent/30"></div>
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12 bg-surface-2 rounded-xl">
          <Flag className="w-12 h-12 text-ink-subtle mx-auto mb-4" />
          <h3 className="text-lg font-medium text-ink mb-2">
            {t('reports.noReports')}
          </h3>
          <p className="text-ink-muted">
            {filter === 'all' ? t('reports.noReportsDesc') : t('reports.noReportsFilter')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => {
            const StatusIcon = STATUS_CONFIG[report.status].icon;
            return (
              <div
                key={report.id}
                className="bg-surface rounded-xl shadow-sm border border-hairline overflow-hidden"
              >
                <div className="p-4">
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[report.status].color}`}>
                        <StatusIcon className="w-3.5 h-3.5" />
                        {t(`reports.status.${report.status}`)}
                      </span>
                      <span className="px-2 py-1 bg-surface-2 rounded text-xs text-ink-muted">
                        {report.contentType === 'post' ? '📸 Post' : '💬 Comment'}
                      </span>
                    </div>
                    <span className="text-xs text-ink-subtle">
                      {format(report.createdAt.toDate(), 'PPp', { locale: dateLocale })}
                    </span>
                  </div>

                  {/* Reason */}
                  <div className="mb-3">
                    <span className="text-sm font-medium text-ink">
                      {t('reports.reason')}:
                    </span>
                    <span className="ml-2 text-sm text-danger font-medium">
                      {getReasonLabel(report.reason)}
                    </span>
                  </div>

                  {/* Additional Details */}
                  {report.additionalDetails && (
                    <div className="mb-3 p-3 bg-surface-2 rounded-lg">
                      <p className="text-sm text-ink-muted">
                        "{report.additionalDetails}"
                      </p>
                    </div>
                  )}

                  {/* Meta Info */}
                  <div className="flex flex-wrap gap-4 text-xs text-ink-subtle mb-3">
                    <span>
                      <strong>{t('reports.reportedBy')}:</strong> {report.reporterEmail || report.reportedBy}
                    </span>
                    <span>
                      <strong>{t('reports.contentId')}:</strong> {report.contentId.slice(0, 8)}...
                    </span>
                    {report.reviewedAt && (
                      <span>
                        <strong>{t('reports.reviewedAt')}:</strong> {format(report.reviewedAt.toDate(), 'PPp', { locale: dateLocale })}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  {report.status === 'pending' && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-hairline">
                      <button
                        onClick={() => handleUpdateStatus(report.id, 'dismissed')}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-surface-2 text-ink-muted rounded-lg hover:bg-surface-3 transition-colors"
                      >
                        <X className="w-4 h-4" />
                        {t('reports.dismiss')}
                      </button>
                      <button
                        onClick={() => handleUpdateStatus(report.id, 'reviewed')}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-info/10 text-info rounded-lg hover:bg-info/20 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        {t('reports.markReviewed')}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedReport(report);
                          setActionModalOpen(true);
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-danger/10 text-danger rounded-lg hover:bg-danger/20 transition-colors"
                      >
                        <Ban className="w-4 h-4" />
                        {t('reports.takeAction')}
                      </button>
                    </div>
                  )}

                  {/* Show action taken */}
                  {report.action && (
                    <div className="mt-3 p-2 bg-danger/10 rounded-lg">
                      <span className="text-xs text-danger">
                        <strong>{t('reports.actionTaken')}:</strong> {report.action}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Action Modal */}
      {actionModalOpen && selectedReport && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-surface rounded-xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-hairline">
              <h2 className="text-lg font-semibold">
                {t('reports.takeActionTitle')}
              </h2>
              <button
                onClick={() => {
                  setActionModalOpen(false);
                  setSelectedReport(null);
                }}
                className="p-1 hover:bg-surface-2 rounded-full"
              >
                <X className="w-5 h-5 text-ink-muted" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-ink-muted mb-4">
                {t('reports.selectAction')}
              </p>
              <button
                onClick={() => handleUpdateStatus(selectedReport.id, 'actioned', 'content_removed')}
                className="w-full flex items-center gap-3 p-3 bg-danger/10 text-danger rounded-lg hover:bg-danger/20 transition-colors"
              >
                <Trash2 className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-medium">{t('reports.actions.removeContent')}</div>
                  <div className="text-xs opacity-75">{t('reports.actions.removeContentDesc')}</div>
                </div>
              </button>
              <button
                onClick={() => handleUpdateStatus(selectedReport.id, 'actioned', 'user_warned')}
                className="w-full flex items-center gap-3 p-3 bg-accent/10 text-accent rounded-lg hover:bg-accent/20 transition-colors"
              >
                <AlertTriangle className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-medium">{t('reports.actions.warnUser')}</div>
                  <div className="text-xs opacity-75">{t('reports.actions.warnUserDesc')}</div>
                </div>
              </button>
              <button
                onClick={() => handleUpdateStatus(selectedReport.id, 'actioned', 'user_banned')}
                className="w-full flex items-center gap-3 p-3 bg-surface-2 text-ink-muted rounded-lg hover:bg-surface-3 transition-colors"
              >
                <Ban className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-medium">{t('reports.actions.banUser')}</div>
                  <div className="text-xs opacity-75">{t('reports.actions.banUserDesc')}</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
